import {
  DECIMAL_TIME_REGEX,
  MAX_HOURS_TIME_INPUT_12H,
  MAX_HOURS_TIME_INPUT_24H,
  MINUTES_MAX,
  WORDED_TIME_REGEX,
  WORDED_TIME_VALIDATION_REGEX,
} from "../constants/timeEntry";
import dropRight from "lodash/dropRight";

class TimeEntryUtils {
  getHoursDiff(fromTime: string, toTime: string): string {
    if (!fromTime || !toTime) return "0";

    const hoursDiffFloat = this.convertTimeToNumber(toTime) - this.convertTimeToNumber(fromTime);

    return (Math.round(hoursDiffFloat * 100) / 100).toString();
  }

  hoursMinutesToDecimal(h: number, m: number) {
    return h + (m < MINUTES_MAX ? m / MINUTES_MAX : 0);
  }

  decimalTimeInFloat(hours: string): number {
    return +hours.replace(",", ".");
  }

  getHoursFloat(hours: string): number {
    if (DECIMAL_TIME_REGEX.test(hours)) {
      return this.decimalTimeInFloat(hours);
    }

    return this.convertWordedTimeToFloat(hours);
  }

  convertWordedTimeToFloat(hours: string): number {
    const numbers = hours?.match(WORDED_TIME_REGEX);
    if (!numbers || !numbers.length || !WORDED_TIME_VALIDATION_REGEX.test(hours)) return 0;

    const time = { h: +numbers[0], m: 0 };
    if (numbers.length > 1) time.m = +numbers[1];

    return this.hoursMinutesToDecimal(time.h, time.m);
  }

  formatTime(time: string, useMeridiem: boolean) {
    if (!time) return "";

    const { hours, minutes, meridiem } = this.extractTimePartsFromValue(time, useMeridiem);
    return `${hours}:${minutes} ${meridiem}`.trim();
  }

  arrayTime(time: string): number[] {
    return time.split(":").map(Number);
  }

  padTimePart(timePart: number | string): string {
    return String(timePart).padStart(2, "0");
  }

  extractTimePartsFromValue(time: string | null, useMeridiem = false) {
    const timeParts = {
      hours: "",
      minutes: "",
      meridiem: "",
    };
    if (!time) return timeParts;

    const [hours, minutes] = this.arrayTime(time);
    timeParts.minutes = this.padTimePart(minutes);

    if (useMeridiem) {
      timeParts.hours = this.padTimePart(hours > 12 ? hours - 12 : hours);
      timeParts.meridiem = hours >= 12 ? "pm" : "am";
    } else {
      timeParts.hours = this.padTimePart(hours);
    }

    return timeParts;
  }

  convertNumberToTime(timeDecimal: number): string {
    const hours = Math.floor(timeDecimal);
    const minutes = Math.round((timeDecimal - hours) * MINUTES_MAX);
    return `${this.padTimePart(hours)}:${this.padTimePart(minutes)}`;
  }

  convertTimeToNumber(time: string): number {
    const [hours, minutes] = this.arrayTime(time);
    return hours + minutes / MINUTES_MAX;
  }

  getCurrentHHmm(): string {
    const now = new Date();
    const formattedHours = this.padTimePart(now.getHours());
    const formattedMinutes = this.padTimePart(now.getMinutes());

    return formattedHours + ":" + formattedMinutes;
  }

  useMeridiem(timeFormat: string): boolean {
    return timeFormat.toLowerCase().includes("a");
  }

  getHoursOptions(useMeridiem: boolean) {
    const maxHours = useMeridiem ? MAX_HOURS_TIME_INPUT_12H : MAX_HOURS_TIME_INPUT_24H;
    if (useMeridiem) {
      const twelveHours = Array.from({ length: maxHours }, (_, i) => this.padTimePart(i + 1));
      return dropRight([this.padTimePart(12), ...twelveHours]);
    }

    return Array.from({ length: maxHours + 1 }, (_, i) => this.padTimePart(i));
  }

  get minutesOptions() {
    return Array.from({ length: 60 }, (_, i) => this.padTimePart(i));
  }
}

export default new TimeEntryUtils();
